import { useState, useEffect } from 'react';
import { eventsRequest } from '../utils/eventsRequest';
import {
  SESSION_KEY_LOGO_URL,
  SESSION_KEY_COMPANY_NAME,
  SESSION_KEY_WEBSITE,
  SESSION_KEY_PHONE,
  SESSION_KEY_EMAIL,
  SESSION_KEY_ADDRESS,
  SESSION_KEY_COUNTRY_CODE,
  SESSION_KEY_POSTAL
} from '../constants';

interface PublicSettingsResponse {
  data: {
    companyName: string;
    logoUrl: string;
    website: string;
    phone: string;
    email: string;
    address: string;
    countryCode: string;
    postal: string;
  }
}

export const usePublicSettings = () => {
  const getInitialValue = (key: string) => {
    const value = localStorage.getItem(key);
    return value === null ? null : value;
  };

  const [logoUrl, setLogoUrl] = useState<string | null>(() => getInitialValue(SESSION_KEY_LOGO_URL));
  const [companyName, setCompanyName] = useState<string | null>(() => getInitialValue(SESSION_KEY_COMPANY_NAME));
  const [website, setWebsite] = useState<string | null>(() => getInitialValue(SESSION_KEY_WEBSITE));
  const [phone, setPhone] = useState<string | null>(() => getInitialValue(SESSION_KEY_PHONE));
  const [email, setEmail] = useState<string | null>(() => getInitialValue(SESSION_KEY_EMAIL));
  const [address, setAddress] = useState<string | null>(() => getInitialValue(SESSION_KEY_ADDRESS));
  const [countryCode, setCountryCode] = useState<string | null>(() => getInitialValue(SESSION_KEY_COUNTRY_CODE));
  const [postal, setPostal] = useState<string | null>(() => getInitialValue(SESSION_KEY_POSTAL));

  useEffect(() => {
    if (companyName) {
      document.title = companyName + ' Events';
    }

    if (logoUrl === null || companyName === null) {
      const fetchPublicSettings = async () => {
        try {
          const response = await eventsRequest<PublicSettingsResponse>('/public-settings', 'get');
          if (response.status === 200 || response.status === 201) {
            const {
              logoUrl: fetchedLogoUrl,
              companyName: fetchedCompanyName,
              website: fetchedWebsite,
              phone: fetchedPhone,
              email: fetchedEmail,
              address: fetchedAddress,
              countryCode: fetchedCountryCode,
              postal: fetchedPostal
            } = response.data.data;

            localStorage.setItem(SESSION_KEY_LOGO_URL, fetchedLogoUrl);
            localStorage.setItem(SESSION_KEY_COMPANY_NAME, fetchedCompanyName);
            localStorage.setItem(SESSION_KEY_WEBSITE, fetchedWebsite);
            localStorage.setItem(SESSION_KEY_PHONE, fetchedPhone);
            localStorage.setItem(SESSION_KEY_EMAIL, fetchedEmail);
            localStorage.setItem(SESSION_KEY_ADDRESS, fetchedAddress);
            localStorage.setItem(SESSION_KEY_COUNTRY_CODE, fetchedCountryCode);
            localStorage.setItem(SESSION_KEY_POSTAL, fetchedPostal);

            setLogoUrl(fetchedLogoUrl);
            setCompanyName(fetchedCompanyName);
            setWebsite(fetchedWebsite);
            setPhone(fetchedPhone);
            setEmail(fetchedEmail);
            setAddress(fetchedAddress);
            setCountryCode(fetchedCountryCode);
            setPostal(fetchedPostal);

            document.title = fetchedCompanyName + ' Events';
          }
        } catch (error) {
          console.error("Failed to fetch settings:", error);
        }
      };

      fetchPublicSettings();
    }
  }, [logoUrl, companyName, website, phone, email, address, countryCode, postal]);

  return { logoUrl, companyName, website, phone, email, address, countryCode, postal };
};
